import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

export const InnerPackVisualizer = ({ result }) => {
    const containerRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const frameRef = useRef(null);
    const mainObjectRef = useRef(null);

    // Initial Setup
    useEffect(() => {
        if (!containerRef.current) return;

        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x1c1917); // Dark gray
        sceneRef.current = scene;

        // Camera
        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 2000);
        camera.position.set(200, 200, 200);
        camera.lookAt(0, 0, 0);
        cameraRef.current = camera;

        // Renderer
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(width, height);
        renderer.shadowMap.enabled = true;

        // Mount
        while (containerRef.current.firstChild) {
            containerRef.current.removeChild(containerRef.current.firstChild);
        }
        containerRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(100, 200, 100);
        directionalLight.castShadow = true;
        scene.add(directionalLight);

        // Animation Loop
        const animate = () => {
            frameRef.current = requestAnimationFrame(animate);
            if (mainObjectRef.current) {
                mainObjectRef.current.rotation.y += 0.005;
            }
            renderer.render(scene, camera);
        };
        animate();

        // Cleanup
        const container = containerRef.current;
        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
            if (rendererRef.current && container) {
                container.removeChild(rendererRef.current.domElement);
                rendererRef.current.dispose();
            }
        };
    }, []);

    // Resize Handler
    useEffect(() => {
        const handleResize = () => {
            if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
            const width = containerRef.current.clientWidth;
            const height = containerRef.current.clientHeight;
            cameraRef.current.aspect = width / height;
            cameraRef.current.updateProjectionMatrix();
            rendererRef.current.setSize(width, height);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Update Scene Content
    useEffect(() => {
        if (!sceneRef.current || !result) return;

        // Clear previous
        if (mainObjectRef.current) {
            sceneRef.current.remove(mainObjectRef.current);
            mainObjectRef.current = null;
        }

        // Logic check
        if (!result.dims || !result.cartonDims) return;

        const parent = new THREE.Group();
        const scale = 0.5; // Scale down to fit view

        const cW = result.cartonDims.w * scale;
        const cH = result.cartonDims.h * scale;
        const cL = result.cartonDims.l * scale;

        const uW = result.dims.w * scale;
        const uH = result.dims.h * scale;
        const uL = result.dims.l * scale;

        // 1. Carton (Transparent Wireframe-ish)
        const cartonGeometry = new THREE.BoxGeometry(cW, cH, cL);
        const cartonMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            wireframe: true,
            transparent: true,
            opacity: 0.3
        });
        const carton = new THREE.Mesh(cartonGeometry, cartonMaterial);
        parent.add(carton);

        // 2. Units (Filled)
        // Assume simple grid for now based on max fit
        // If result.arrangement string "5 x 4 x 2" exists, parse it.
        let countW = 1, countL = 1, countH = 1;

        if (result.arrangement) {
            const parts = result.arrangement.split('x').map(s => parseInt(s.trim()));
            if (parts.length === 3) {
                // Note: Order in string might depend on implementation. 
                // Assuming standard W x L x H output from backend logic?
                // Usually code produced `${countW} x ${countL} x ${countH}`
                countW = parts[0];
                countL = parts[1];
                countH = parts[2];
            }
        }

        // Ensure not zero
        countW = countW || 1;
        countL = countL || 1;
        countH = countH || 1;

        const unitGeometry = new THREE.BoxGeometry(uW - 1, uH - 1, uL - 1); // Gap
        const unitMaterial = new THREE.MeshStandardMaterial({
            color: 0xa855f7, // Purple
            roughness: 0.4,
            metalness: 0.1
        });

        const startX = -cW / 2 + uW / 2;
        const startY = -cH / 2 + uH / 2;
        const startZ = -cL / 2 + uL / 2;

        for (let x = 0; x < countW; x++) {
            for (let y = 0; y < countH; y++) {
                for (let z = 0; z < countL; z++) {
                    const unit = new THREE.Mesh(unitGeometry, unitMaterial);
                    unit.position.set(
                        startX + x * uW,
                        startY + y * uH,
                        startZ + z * uL
                    );
                    parent.add(unit);
                }
            }
        }

        sceneRef.current.add(parent);
        mainObjectRef.current = parent;

        // Adjust Camera to Fit
        const maxDim = Math.max(cW, cH, cL);
        if (cameraRef.current) {
            cameraRef.current.position.set(maxDim * 2, maxDim * 2, maxDim * 2);
            cameraRef.current.lookAt(0, 0, 0);
        }

    }, [result]);

    return (
        <div className="h-full w-full relative bg-stone-900 rounded-lg overflow-hidden z-10">
            <div ref={containerRef} className="w-full h-full" />
            {!result && (
                <div className="absolute inset-0 flex items-center justify-center text-stone-500">
                    No calculation
                </div>
            )}
        </div>
    );
};
